import { useEffect, useRef, useState } from "react";
import { Loader2, Play, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { playCoin, playTick, playFanfare, confetti } from "@/lib/celebrate";
import { useSkill, SkillInfo, SkillTop, SkillResult } from "./SkillShell";

const RULES = ["Tippe auf das Feld, sobald es grün wird – nicht vorher!", "Es gibt 5 Runden, gewertet wird deine Durchschnittszeit.", "Zu früh getippt? Die Runde startet einfach neu."];
const STYLES = {
  wait: ["bg-gradient-to-br from-amber-300 to-amber-500 text-amber-950", "Warte auf Grün …"],
  go: ["bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-[0_0_40px_rgba(16,185,129,.55)]", "JETZT TIPPEN!"],
  early: ["bg-gradient-to-br from-rose-400 to-rose-600 text-white", "Zu früh! Gleich nochmal …"],
};

export function ReactionGame() {
  const { ov, load } = useSkill();
  const [s, setS] = useState(null);
  const [phase, setPhase] = useState("wait");
  const [result, setResult] = useState(null);
  const timer = useRef(null), goAt = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);
  const arm = (delay) => { setPhase("wait"); timer.current = setTimeout(() => { goAt.current = performance.now(); setPhase("go"); playTick(); }, delay); };
  const start = async () => {
    try {
      const { data } = await api.post("/skill/reaction/start");
      setResult(null); setS({ ...data, times: [] }); arm(data.delays[0]);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const finish = async (times) => {
    setPhase("send");
    try {
      const { data } = await api.post(`/skill/reaction/${s.id}/finish`, { times });
      playFanfare(); confetti({ count: 80 }); setResult(data); setS(null); load();
    } catch (e) { toast.error(formatErr(e)); setS(null); }
  };
  const tap = () => {
    if (!s || phase === "early" || phase === "send") return;
    if (phase === "wait") {
      clearTimeout(timer.current); setPhase("early"); navigator.vibrate?.(80);
      timer.current = setTimeout(() => arm(s.delays[s.times.length]), 1000);
      return;
    }
    const t = Math.max(150, Math.round(performance.now() - goAt.current));
    const times = [...s.times, t];
    playCoin(Math.min(times.length * 2, 12));
    setS((p) => ({ ...p, times }));
    if (times.length === 5) finish(times); else arm(s.delays[times.length]);
  };
  if (!ov) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  const g = ov.games.reaction;
  const [cls, label] = STYLES[phase] || STYLES.wait;
  return (
    <div className="space-y-4" data-testid="reaction-game">
      <SkillInfo game="reaction" g={g} rules={RULES} />
      {result ? <SkillResult game="reaction" result={result} onAgain={start} /> : !s ? (
        <button type="button" onClick={start} data-testid="reaction-start" className="tap w-full h-14 rounded-full pink-gradient text-white font-extrabold text-lg pink-glow inline-flex items-center justify-center gap-2"><Play className="w-5 h-5" fill="currentColor" /> Spiel starten</button>
      ) : (
        <div className="space-y-3">
          <button type="button" onPointerDown={tap} data-testid="reaction-pad" data-phase={phase}
            className={`w-full aspect-[4/3] rounded-[2rem] flex flex-col items-center justify-center gap-3 select-none touch-manipulation transition-[background-color,box-shadow] duration-150 ${cls}`}>
            {phase === "send" ? <Loader2 className="w-10 h-10 animate-spin" /> : <Zap className={`w-12 h-12 ${phase === "go" ? "scale-125" : ""} transition-transform`} fill="currentColor" />}
            <span className="font-display text-2xl font-black" data-testid="reaction-label">{phase === "send" ? "Wird gewertet …" : label}</span>
            <span className="text-sm font-bold opacity-80">Runde {Math.min(s.times.length + 1, 5)} von 5</span>
          </button>
          <div className="grid grid-cols-5 gap-2" data-testid="reaction-times">
            {Array.from({ length: 5 }, (_, i) => <span key={i} className={`h-10 rounded-xl flex items-center justify-center text-sm font-extrabold tabular-nums ${s.times[i] ? "bg-emerald-50 text-emerald-700" : "bg-white border border-pink-100 text-slate-300"}`}>{s.times[i] ? `${s.times[i]}` : "–"}</span>)}
          </div>
        </div>
      )}
      <SkillTop game="reaction" g={g} />
    </div>
  );
}
